// src/pages/AuthPage.jsx
// Login + Register page — matches the existing dark dashboard aesthetic.
// Uses useAuth() hook for all auth actions.

import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import PasswordInput from "../components/PasswordInput";
import AuthShell from "../components/AuthShell";
import useTilt3D from "../motion/useTilt3D";

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');

  .auth-card {
    width: 100%;
    max-width: 440px;
    background: rgba(255,255,255,0.035);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 24px;
    padding: clamp(18px, 3.5vh, 40px) clamp(20px, 5vw, 40px);
    backdrop-filter: blur(20px);
    box-shadow: 0 24px 64px rgba(0,0,0,0.4);
    position: relative;
    z-index: 1;
    animation: cardIn 0.5s cubic-bezier(0.16,1,0.3,1) both;
    overflow: hidden;
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
  }

  div.auth-card-glare {
    position: absolute;
    inset: 0;
    border-radius: 24px;
    pointer-events: none;
    transition: opacity 0.3s ease;
    z-index: 0;
  }

  .auth-card > * { position: relative; z-index: 1; }

  @keyframes cardIn {
    from { opacity: 0; transform: translateY(24px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .auth-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: clamp(14px, 3vh, 32px);
  }

  .auth-logo-icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #ff6a52 0%, #c81f30 100%);
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 700; color: #f5f0eb;
    box-shadow: 0 0 20px rgba(255,106,82,0.4);
    flex-shrink: 0;
  }

  .auth-logo-text {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 16px; font-weight: 800;
    color: #efe7df;
    letter-spacing: -0.02em;
  }

  .auth-logo-sub {
    font-size: 11px;
    color: #6b6058;
    margin-top: 1px;
  }

  .auth-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 24px; font-weight: 800;
    color: #efe7df;
    letter-spacing: -0.03em;
    margin-bottom: 6px;
    background: linear-gradient(135deg, #efe7df 0%, #ffb199 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .auth-subtitle {
    font-size: 13px;
    color: #9a908a;
    margin-bottom: clamp(14px, 3vh, 28px);
    line-height: 1.5;
  }

  /* Tab switcher */
  .auth-tabs {
    display: flex;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    padding: 4px;
    margin-bottom: clamp(14px, 3vh, 28px);
    gap: 4px;
  }

  .auth-tab {
    flex: 1;
    padding: 9px;
    border-radius: 9px;
    border: none;
    font-family: 'Inter', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .auth-tab.active {
    background: linear-gradient(135deg, #ff6a52 0%, #c81f30 100%);
    color: #f5f0eb;
    box-shadow: 0 0 16px rgba(255,106,82,0.35);
  }

  .auth-tab.inactive {
    background: transparent;
    color: #9a908a;
  }

  .auth-tab.inactive:hover {
    background: rgba(255,255,255,0.06);
    color: #efe7df;
  }

  /* Form fields */
  .auth-field {
    margin-bottom: clamp(10px, 2vh, 16px);
  }

  .auth-label {
    display: block;
    font-size: 12px; font-weight: 600;
    color: #9a908a;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }

  .auth-input {
    width: 100%;
    height: clamp(38px, 6vh, 44px);
    padding: 0 14px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #efe7df;
    outline: none;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }

  .auth-input:focus {
    border-color: rgba(255,106,82,0.6);
    background: rgba(255,106,82,0.07);
    box-shadow: 0 0 0 3px rgba(255,106,82,0.12);
  }

  .auth-input::placeholder { color: #6b6058; }

  /* Remember me row */
  .auth-remember {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: clamp(14px, 3vh, 24px);
    cursor: pointer;
    user-select: none;
  }

  .auth-checkbox {
    width: 18px; height: 18px;
    border-radius: 5px;
    border: 1px solid rgba(255,255,255,0.15);
    background: rgba(255,255,255,0.05);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
    cursor: pointer;
  }

  .auth-checkbox.checked {
    background: linear-gradient(135deg, #ff6a52, #c81f30);
    border-color: transparent;
    box-shadow: 0 0 10px rgba(255,106,82,0.4);
  }

  .auth-remember-label {
    font-size: 13px;
    color: #9a908a;
  }

  /* Primary button */
  .auth-btn {
    width: 100%;
    height: 46px;
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, #ff6a52 0%, #c81f30 100%);
    color: #f5f0eb;
    font-family: 'Inter', sans-serif;
    font-size: 14px; font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.1s, box-shadow 0.15s;
    box-shadow: 0 0 24px rgba(255,106,82,0.35);
    margin-bottom: 16px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }

  .auth-btn:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 32px rgba(255,106,82,0.45);
  }

  .auth-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* Divider */
  .auth-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    color: #6b6058;
    font-size: 12px;
  }

  .auth-divider::before,
  .auth-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,0.07);
  }

  /* Slack button */
  .auth-slack-btn {
    width: 100%;
    height: 46px;
    border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.1);
    background: rgba(74,21,75,0.25);
    color: #efe7df;
    font-family: 'Inter', sans-serif;
    font-size: 14px; font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 10px;
  }

  .auth-slack-btn:hover {
    background: rgba(74,21,75,0.5);
    border-color: rgba(74,21,75,0.6);
    transform: translateY(-1px);
  }

  /* Error banner */
  .auth-error {
    padding: 12px 14px;
    border-radius: 10px;
    background: rgba(255,77,94,0.1);
    border: 1px solid rgba(255,77,94,0.25);
    color: #ff4d5e;
    font-size: 13px;
    margin-bottom: 16px;
    animation: shake 0.3s ease;
  }

  /* Forgot password link */
  .auth-forgot-link {
    display: block;
    text-align: right;
    font-size: 12px;
    color: #9a908a;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin: 8px 0 20px;
    font-family: 'Inter', sans-serif;
    transition: color 0.15s;
  }

  .auth-forgot-link:hover {
    color: #ffb199;
  }

  .auth-back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #9a908a;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-bottom: 20px;
    font-family: 'Inter', sans-serif;
    transition: color 0.15s;
  }

  .auth-back-link:hover {
    color: #ffb199;
  }


  @media (max-width: 768px) {
    .auth-card { border-radius: 20px; }
    .auth-title { font-size: 22px; }
  }
  @media (max-width: 480px) {
    .auth-card {
      border-radius: 16px;
    }
    .auth-title { font-size: 20px; }
    .auth-input { height: 40px; font-size: 13px; }
    .auth-btn, .auth-slack-btn { height: 42px; font-size: 13px; }
  }
  @keyframes shake {
    0%,100% { transform: translateX(0); }
    25%      { transform: translateX(-6px); }
    75%      { transform: translateX(6px); }
  }

  /* Footer links */
  .auth-footer-wrapper {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    padding: 12px 24px 14px;
    background: rgba(10, 7, 6, 0.85);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-top: 1px solid rgba(255, 106, 82, 0.22);
    box-shadow:
      0 -1px 0 rgba(255, 106, 82, 0.12),
      0 -8px 32px rgba(255, 106, 82, 0.06),
      inset 0 1px 0 rgba(255, 106, 82, 0.08);
  }

  .auth-footer-wrapper::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 280px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 106, 82, 0.75) 50%,
      transparent 100%
    );
    box-shadow: 0 0 16px 3px rgba(255, 106, 82, 0.35);
  }

  .auth-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-wrap: wrap;
    text-align: center;
    font-size: 12px;
  }

  .auth-footer a {
    color: #8a8078;
    text-decoration: none;
    padding: 4px 12px;
    border-radius: 20px;
    border: 1px solid transparent;
    transition: all 0.22s ease;
    letter-spacing: 0.02em;
  }

  .auth-footer a:hover {
    color: #ffb199;
    border-color: rgba(255, 106, 82, 0.4);
    background: rgba(255, 106, 82, 0.1);
    box-shadow:
      0 0 14px rgba(255, 106, 82, 0.22),
      inset 0 0 10px rgba(255, 106, 82, 0.07);
    text-shadow: 0 0 10px rgba(255, 177, 153, 0.5);
  }

  .auth-footer-sep {
    color: rgba(255, 106, 82, 0.4);
    font-size: 16px;
    line-height: 1;
    user-select: none;
    margin: 0 2px;
  }

  /* Success banner */
  .auth-success {
    padding: 12px 14px;
    border-radius: 10px;
    background: rgba(63,174,125,0.1);
    border: 1px solid rgba(63,174,125,0.25);
    color: #3fae7d;
    font-size: 13px;
    margin-bottom: 16px;
  }

  /* Spinner */
  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #f5f0eb;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    flex-shrink: 0;
  }
`;

// ── Slack Logo SVG ────────────────────────────────────────────────────────────
function SlackLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 122.8 122.8" xmlns="http://www.w3.org/2000/svg">
      <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#E01E5A"/>
      <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36C5F0"/>
      <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2EB67D"/>
      <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ECB22E"/>
    </svg>
  );
}

// ── Auth Page ─────────────────────────────────────────────────────────────────
export default function AuthPage({ onAuthSuccess }) {
  const { login, register, loginWithSlack, forgotPassword } = useAuth();

  const [tab, setTab]               = useState("login");  // "login" | "register" | "forgot"
  const [name, setName]             = useState("");
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);

  const { ref: tiltRef, style: tiltStyle, glareStyle, onPointerMove, onPointerLeave } = useTilt3D({ max: 3, scale: 1.004 });

  // Measure the fixed footer bar so the shell can reserve exactly that
  // much space — otherwise on shorter viewports it overlaps the bottom
  // of the card/hero instead of sitting cleanly below them.
  const footerRef = useRef(null);
  const [footerH, setFooterH] = useState(0);
  useEffect(() => {
    const measure = () => setFooterH(footerRef.current?.offsetHeight || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const switchTab = (t) => {
    setTab(t);
    setError(null);
    setSuccess(null);
  };

  const heroCopy = {
    login: {
      eyebrow: "AI WORKFLOW COORDINATOR",
      headline: "Your Slack threads, already sorted into a plan.",
      subtext: "Sign in to pick up right where your team left off — every task extracted, assigned, and tracked automatically.",
    },
    register: {
      eyebrow: "AI WORKFLOW COORDINATOR",
      headline: "Stop hunting for tasks buried in Slack.",
      subtext: "Create your workspace in under two minutes. No dedicated PM required — the AI reads the channel so you don't have to.",
    },
    forgot: {
      eyebrow: "AI WORKFLOW COORDINATOR",
      headline: "We'll get you back in.",
      subtext: "Enter the email on your account and a reset link will land in your inbox shortly.",
    },
  }[tab];

  const handleSubmit = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (tab === "forgot") {
        await forgotPassword({ email });
        setSuccess("If an account exists for that email, a reset link is on its way. Check your inbox.");
      } else if (tab === "login") {
        const data = await login({ email, password, rememberMe });
        onAuthSuccess(data.user);
      } else {
        // Register → then auto-login → send to onboarding
        await register({ name, email, password });
        const data = await login({ email, password, rememberMe: false });
        setSuccess("Account created! Setting up your workspace…");
        setTimeout(() => onAuthSuccess(data.user, true), 800);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [tab, name, email, password, rememberMe, login, register, forgotPassword, onAuthSuccess]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <>
      <style>{STYLES}</style>
      <AuthShell eyebrow={heroCopy.eyebrow} headline={heroCopy.headline} subtext={heroCopy.subtext} bottomInset={footerH + 8}>
        <div
          className="auth-card"
          ref={tiltRef}
          style={{ ...tiltStyle, maxHeight: `calc(100dvh - 32px - ${footerH}px)` }}
          onPointerMove={onPointerMove}
          onPointerLeave={onPointerLeave}
        >
          <div className="auth-card-glare" style={glareStyle} />

          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-icon">AI</div>
            <div>
              <div className="auth-logo-text">AI Workflow</div>
              <div className="auth-logo-sub">Coordinator</div>
            </div>
          </div>

          {/* Title */}
          <div className="auth-title">
            {tab === "login" ? "Welcome back" : tab === "register" ? "Create your account" : "Reset your password"}
          </div>
          <div className="auth-subtitle">
            {tab === "login"
              ? "Sign in to access your personal dashboard."
              : tab === "register"
              ? "Join AI Workflow Coordinator and manage your tasks smarter."
              : "Enter your email and we'll send you a link to reset your password."}
          </div>

          {/* Tab switcher (hidden on the forgot-password screen) */}
          {tab !== "forgot" && (
            <div className="auth-tabs" role="tablist">
              <button
                role="tab"
                aria-selected={tab === "login"}
                className={`auth-tab ${tab === "login" ? "active" : "inactive"}`}
                onClick={() => switchTab("login")}
              >Sign In</button>
              <button
                role="tab"
                aria-selected={tab === "register"}
                className={`auth-tab ${tab === "register" ? "active" : "inactive"}`}
                onClick={() => switchTab("register")}
              >Create Account</button>
            </div>
          )}

          {/* Back link (forgot-password screen only) */}
          {tab === "forgot" && (
            <button className="auth-back-link" onClick={() => switchTab("login")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Sign In
            </button>
          )}

          {/* Error / Success banners */}
          {error   && <div className="auth-error"   role="alert">⚠ {error}</div>}
          {success && <div className="auth-success" role="status">✓ {success}</div>}

          {/* Name field (register only) */}
          {tab === "register" && (
            <div className="auth-field">
              <label className="auth-label">Full Name</label>
              <input
                className="auth-input"
                type="text"
                placeholder="Jane Smith"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="name"
              />
            </div>
          )}

          {/* Email */}
          <div className="auth-field">
            <label className="auth-label">Email</label>
            <input
              className="auth-input"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="email"
            />
          </div>

          {/* Password (login / register only — not shown on forgot-password) */}
          {tab !== "forgot" && (
            <div className="auth-field" style={{ marginBottom: tab === "login" ? 0 : 16 }}>
              <label className="auth-label">Password</label>
              <PasswordInput
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tab === "register" ? "Min. 8 characters" : "••••••••"}
                autoComplete={tab === "login" ? "current-password" : "new-password"}
              />
            </div>
          )}

          {/* Forgot password link (login only) */}
          {tab === "login" && (
            <button className="auth-forgot-link" onClick={() => switchTab("forgot")}>
              Forgot password?
            </button>
          )}

          {/* Remember Me (login only) */}
          {tab === "login" && (
            <div
              className="auth-remember"
              onClick={() => setRememberMe(v => !v)}
              role="checkbox"
              aria-checked={rememberMe}
              tabIndex={0}
              onKeyDown={e => e.key === " " && setRememberMe(v => !v)}
            >
              <div className={`auth-checkbox ${rememberMe ? "checked" : ""}`}>
                {rememberMe && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l2.5 2.5L9 1" stroke="#f5f0eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="auth-remember-label">
                Remember me for 30 days
              </span>
            </div>
          )}

          {/* Submit button */}
          <button
            className="auth-btn"
            onClick={handleSubmit}
            disabled={loading}
            style={{ marginTop: tab === "register" ? 8 : 0 }}
          >
            {loading
              ? <><div className="spinner" />{tab === "login" ? "Signing in…" : tab === "register" ? "Creating account…" : "Sending reset link…"}</>
              : tab === "login" ? "Sign In" : tab === "register" ? "Create Account" : "Send Reset Link"
            }
          </button>

          {/* Divider + Slack (hidden on forgot-password screen) */}
          {tab !== "forgot" && (
            <>
              <div className="auth-divider">or continue with</div>
              <button className="auth-slack-btn" onClick={loginWithSlack}>
                <SlackLogo />
                Continue with Slack
              </button>
            </>
          )}

        </div>
      </AuthShell>

      {/* Footer links */}
      <div className="auth-footer-wrapper" ref={footerRef}>
        <div className="auth-footer">
          <a href="/terms">Terms of Service</a>
          <span className="auth-footer-sep">•</span>
          <a href="/privacy">Privacy Policy</a>
          <span className="auth-footer-sep">•</span>
          <a href="/refund">Refund Policy</a>
        </div>
      </div>
    </>
  );
}
